/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { Tooltip } from 'radix-ui';
import React, { useState } from 'react';

import { useAccount } from '~/apis';
import { useTranslation } from '~/app';
import { useIsEmailVerifiedEffective } from '~/app/DebugEmailVerifiedContext';
import { useAuthDispatch, useAuthState } from '~/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~/components/DropdownMenu/DropdownMenu';
import { useLogoutDialog } from '~/components/LogoutDialogContext';
import AccountProfilePicture from '~/components/ProfilePicture/AccountProfilePicture';

import AccountMenuContent from './AccountMenuContent';
import { ReactComponent as LoginIcon } from './login.svg';

const AccountMenuButton = (): JSX.Element => {
  const { t } = useTranslation();
  const { isAuthorized } = useAuthState();
  const authDispatch = useAuthDispatch();
  const { data: account, isFetching } = useAccount();
  const isEmailVerified = useIsEmailVerifiedEffective();
  const [isOpen, setIsOpen] = useState(false);
  const { openLogoutDialog } = useLogoutDialog();
  if (!isAuthorized) {
    return (
      <Tooltip.Provider>
        <Tooltip.Root delayDuration={0}>
          <Tooltip.Trigger asChild>
            <button
              className="relative my-1 flex h-10 transform-gpu cursor-pointer flex-row items-center justify-center rounded-md bg-primary-light px-3 py-2 transition-all hover:scale-[1.02] hover:brightness-125 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden active:scale-[0.98] active:brightness-125"
              onClick={() => authDispatch({ type: 'login' })}
              aria-label={t('start_accountMenuTitle')}
            >
              <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                <LoginIcon className="h-full w-full" />
              </div>
              <div className="hidden pl-2 align-middle text-sm md:inline">
                {t('start_accountLoginButtonText')}
              </div>
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              sideOffset={8}
              className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
            >
              {t('start_accountMenuTitle')}
              <Tooltip.Arrow className="fill-white" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  }
  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <div className="relative">
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative my-1 flex h-10 transform-gpu cursor-pointer flex-row items-center justify-center rounded-md bg-primary-light px-3 py-2 transition-all hover:scale-[1.02] hover:brightness-125 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden active:scale-[0.98] active:brightness-125 md:max-w-88"
                  aria-label={t('start_accountMenuTitle')}
                >
                  {!account?.username && isFetching ? (
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <div className="mr-2 hidden max-w-48 truncate align-middle text-sm whitespace-nowrap md:inline lg:max-w-64">
                      {account?.username?.substr(0, 20) || ''}
                    </div>
                  )}
                  <div className="relative shrink-0">
                    <AccountProfilePicture
                      username={account?.username || ''}
                      size="sm"
                      className="h-7 w-7"
                    />
                    {/* Email verification warning badge */}
                    {account && !isEmailVerified ? (
                      <div className="absolute -top-1 -right-1 z-20">
                        <span
                          className="text-xs text-yellow-500"
                          title={t('verify_email_menu_hint')}
                        >
                          ⚠
                        </span>
                      </div>
                    ) : null}
                    {/* League promotion badge at bottom right */}
                    {account?.toplistPromotion === 1 ? (
                      <div className="absolute -right-1 -bottom-1 z-20">
                        <span className="text-sm text-green-500">&#9650;</span>
                      </div>
                    ) : account?.toplistPromotion === -1 ? (
                      <div className="absolute -right-1 -bottom-1 z-20">
                        <span className="text-sm text-red-500">&#9660;</span>
                      </div>
                    ) : null}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <AccountMenuContent
                  onClose={() => setIsOpen(false)}
                  onRequestLogout={() => {
                    openLogoutDialog();
                    setIsOpen(false);
                  }}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={8}
            className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
          >
            {t('start_accountMenuTitle')}
            <Tooltip.Arrow className="fill-white" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default AccountMenuButton;
